import React, { useState, useEffect } from 'react';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import { FaDollarSign, FaDownload, FaChartLine, FaCalendarAlt } from 'react-icons/fa';

const CommissionReport = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchReport();
  }, [dateRange]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        startDate: dateRange.start,
        endDate: dateRange.end
      });
      const response = await HttpInterceptor.get(`/api/admin/marketplace/commission-report?${params.toString()}`);
      setReportData(response.data.data);
    } catch (error) {
      console.error('Error fetching commission report:', error);
      // Set mock data for now if API doesn't exist
      setReportData({
        totalCommission: 0,
        totalRevenue: 0,
        totalOrders: 0,
        resellerCommissions: [],
        summary: {
          byMonth: [],
          byReseller: []
        }
      });
      toast.error('Failed to load commission report. Showing placeholder data.');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!reportData) return;
    
    const headers = ['Reseller', 'Total Sales', 'Commission Rate', 'Commission Amount', 'Orders'];
    const rows = reportData.resellerCommissions?.map(item => [
      item.resellerName || 'N/A',
      `₹${item.totalSales?.toFixed(2) || '0.00'}`,
      `${item.commissionRate || 10}%`,
      `₹${item.commissionAmount?.toFixed(2) || '0.00'}`,
      item.orderCount || 0
    ]) || [];
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `commission-report-${dateRange.start}-to-${dateRange.end}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Commission report exported to CSV');
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold mb-2">Commission Report</h1>
            <p className="text-gray-600">View platform commission earnings and reseller payouts</p>
          </div>
          <button
            onClick={exportToCSV}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            <FaDownload />
            Export CSV
          </button>
        </div>

        {/* Date Range Filter */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="flex items-center gap-4">
            <FaCalendarAlt className="text-purple-600" />
            <div className="flex items-center gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <button
                onClick={fetchReport}
                className="mt-6 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Apply Filter
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-purple-600">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-gray-600 text-sm font-medium">Total Platform Commission</h3>
              <FaDollarSign className="text-purple-600 text-xl" />
            </div>
            <p className="text-3xl font-bold text-purple-600 mb-1">
              ₹{(reportData?.totalCommission || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
            </p>
            <p className="text-sm text-gray-500">From {reportData?.totalOrders || 0} orders</p>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-600">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-gray-600 text-sm font-medium">Total Revenue</h3>
              <FaChartLine className="text-green-600 text-xl" />
            </div>
            <p className="text-3xl font-bold text-green-600 mb-1">
              ₹{(reportData?.totalRevenue || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
            </p>
            <p className="text-sm text-gray-500">Gross marketplace sales</p>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-600">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-gray-600 text-sm font-medium">Average Commission Rate</h3>
              <FaChartLine className="text-blue-600 text-xl" />
            </div>
            <p className="text-3xl font-bold text-blue-600 mb-1">
              {reportData?.totalRevenue > 0 
                ? ((reportData.totalCommission / reportData.totalRevenue) * 100).toFixed(1)
                : '0.0'
              }%
            </p>
            <p className="text-sm text-gray-500">Platform commission percentage</p>
          </div>
        </div>

        {/* Reseller Commission Details */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold mb-4">Reseller Commission Breakdown</h2>
          {reportData?.resellerCommissions && reportData.resellerCommissions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Reseller</th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">Total Sales</th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">Commission Rate</th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">Commission Amount</th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">Orders</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.resellerCommissions.map((item, index) => (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="p-3 text-sm text-gray-800">{item.resellerName || 'N/A'}</td>
                      <td className="p-3 text-sm text-right text-gray-700">
                        ₹{(item.totalSales || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      </td>
                      <td className="p-3 text-sm text-right text-gray-700">{item.commissionRate || 10}%</td>
                      <td className="p-3 text-sm text-right font-semibold text-purple-600">
                        ₹{(item.commissionAmount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      </td>
                      <td className="p-3 text-sm text-right text-gray-700">{item.orderCount || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <FaChartLine className="text-4xl mx-auto mb-4 text-gray-300" />
              <p>No commission data available for the selected date range</p>
            </div>
          )}
        </div>
      </div>
  );
};

export default CommissionReport;

